import React, { useEffect, useMemo, useRef, useState } from "react";

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "";
const DEFAULT_CENTER = { lat: 22.9734, lng: 78.6569 };
const DEFAULT_ZOOM = 4;
const SELECTED_ZOOM = 13;
const SEARCH_DEBOUNCE_MS = 250;

const BRAND = {
  red: "#D73C2E",
  black: "#252935",
  mix: "#B9343A",
  redSoft: "#fff2ef",
  redSoftAlt: "#fff7f5",
  border: "rgba(185,52,58,0.14)",
  borderStrong: "rgba(185,52,58,0.20)",
  borderSoft: "rgba(185,52,58,0.08)",
  text: "#252935",
  muted: "#6b6f7d",
};

let googleMapsLoaderPromise = null;

function parseCoordinate(value) {
  if (value === undefined || value === null || value === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function formatCoordinate(value) {
  if (!Number.isFinite(Number(value))) return "";
  return Number(value).toFixed(6);
}

function loadGoogleMapsApi(apiKey) {
  if (!apiKey) return Promise.resolve(null);
  if (window.google?.maps) return Promise.resolve(window.google.maps);
  if (googleMapsLoaderPromise) return googleMapsLoaderPromise;

  googleMapsLoaderPromise = new Promise((resolve, reject) => {
    const callbackName = `__jkGoogleMapsInit_${Date.now()}`;
    const existingScript = document.querySelector('script[data-google-maps-loader="true"]');

    window[callbackName] = () => {
      delete window[callbackName];
      resolve(window.google.maps);
    };

    if (existingScript) {
      existingScript.addEventListener("error", () => reject(new Error("Failed to load Google Maps.")), { once: true });
      return;
    }

    const script = document.createElement("script");
    script.async = true;
    script.defer = true;
    script.dataset.googleMapsLoader = "true";
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}&loading=async&libraries=places&callback=${callbackName}&v=weekly`;
    script.onerror = () => {
      delete window[callbackName];
      googleMapsLoaderPromise = null;
      reject(new Error("Failed to load Google Maps."));
    };
    document.head.appendChild(script);
  });

  return googleMapsLoaderPromise;
}

export default function BirthLocationMapField({
  latitude,
  longitude,
  onLatitudeChange,
  onLongitudeChange,
}) {
  const mapNodeRef = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const clickListenerRef = useRef(null);
  const geocoderRef = useRef(null);
  const autocompleteServiceRef = useRef(null);
  const onLatitudeChangeRef = useRef(onLatitudeChange);
  const onLongitudeChangeRef = useRef(onLongitudeChange);
  const suppressAutocompleteRef = useRef(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [mapState, setMapState] = useState({
    loading: false,
    ready: false,
    error: "",
  });
  const [searchState, setSearchState] = useState({
    loading: false,
    error: "",
  });

  const latitudeNumber = useMemo(() => parseCoordinate(latitude), [latitude]);
  const longitudeNumber = useMemo(() => parseCoordinate(longitude), [longitude]);
  const hasCoordinates = latitudeNumber !== null && longitudeNumber !== null;
  const googleMapsLink = hasCoordinates
    ? `https://www.google.com/maps?q=${latitudeNumber},${longitudeNumber}`
    : "";

  useEffect(() => {
    onLatitudeChangeRef.current = onLatitudeChange;
  }, [onLatitudeChange]);

  useEffect(() => {
    onLongitudeChangeRef.current = onLongitudeChange;
  }, [onLongitudeChange]);

  useEffect(() => {
    let cancelled = false;

    if (!GOOGLE_MAPS_API_KEY) {
      setMapState({ loading: false, ready: false, error: "" });
      return undefined;
    }

    setMapState((prev) => ({ ...prev, loading: true, error: "" }));

    loadGoogleMapsApi(GOOGLE_MAPS_API_KEY)
      .then(() => {
        if (cancelled || !mapNodeRef.current || mapRef.current) return;

        mapRef.current = new window.google.maps.Map(mapNodeRef.current, {
          center: DEFAULT_CENTER,
          zoom: DEFAULT_ZOOM,
          streetViewControl: false,
          mapTypeControl: false,
          fullscreenControl: false,
        });
        geocoderRef.current = new window.google.maps.Geocoder();
        autocompleteServiceRef.current = new window.google.maps.places.AutocompleteService();

        clickListenerRef.current = mapRef.current.addListener("click", (event) => {
          const nextLat = Number(event.latLng.lat().toFixed(6));
          const nextLng = Number(event.latLng.lng().toFixed(6));
          onLatitudeChangeRef.current(formatCoordinate(nextLat));
          onLongitudeChangeRef.current(formatCoordinate(nextLng));
        });

        setMapState({ loading: false, ready: true, error: "" });
      })
      .catch((error) => {
        if (cancelled) return;
        setMapState({
          loading: false,
          ready: false,
          error: error?.message || "Google Maps could not be loaded.",
        });
      });

    return () => {
      cancelled = true;
      clickListenerRef.current?.remove?.();
      clickListenerRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (!mapRef.current || !window.google?.maps) return;

    if (!hasCoordinates) {
      if (markerRef.current) {
        markerRef.current.setMap(null);
        markerRef.current = null;
      }
      mapRef.current.setCenter(DEFAULT_CENTER);
      mapRef.current.setZoom(DEFAULT_ZOOM);
      return;
    }

    const position = { lat: latitudeNumber, lng: longitudeNumber };

    if (!markerRef.current) {
      markerRef.current = new window.google.maps.Marker({
        position,
        map: mapRef.current,
        draggable: true,
      });

      markerRef.current.addListener("dragend", (event) => {
        const nextLat = Number(event.latLng.lat().toFixed(6));
        const nextLng = Number(event.latLng.lng().toFixed(6));
        onLatitudeChangeRef.current(formatCoordinate(nextLat));
        onLongitudeChangeRef.current(formatCoordinate(nextLng));
      });
    } else {
      markerRef.current.setPosition(position);
      markerRef.current.setMap(mapRef.current);
    }

    mapRef.current.setCenter(position);
    mapRef.current.setZoom(SELECTED_ZOOM);
  }, [hasCoordinates, latitudeNumber, longitudeNumber]);

  useEffect(() => {
    if (!mapState.ready || !autocompleteServiceRef.current) return undefined;

    if (suppressAutocompleteRef.current) {
      suppressAutocompleteRef.current = false;
      return undefined;
    }

    const query = String(searchQuery || "").trim();
    if (query.length < 2) {
      setSuggestions([]);
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      autocompleteServiceRef.current.getPlacePredictions(
        { input: query },
        (predictions = [], status) => {
          if (status !== window.google.maps.places.PlacesServiceStatus.OK || !predictions.length) {
            setSuggestions([]);
            return;
          }

          setSuggestions(
            predictions.map((prediction) => ({
              placeId: prediction.place_id,
              description: prediction.description,
              primaryText: prediction.structured_formatting?.main_text || prediction.description,
              secondaryText: prediction.structured_formatting?.secondary_text || "",
            }))
          );
          setShowSuggestions(true);
        }
      );
    }, SEARCH_DEBOUNCE_MS);

    return () => window.clearTimeout(timeoutId);
  }, [mapState.ready, searchQuery]);

  const updateCoordinatesFromGeocode = (request) => {
    if (!geocoderRef.current) return;

    setSearchState({ loading: true, error: "" });

    geocoderRef.current.geocode(request, (results, status) => {
      if (status !== "OK" || !Array.isArray(results) || !results.length) {
        setSearchState({
          loading: false,
          error: "No matching place found. Try a more specific location.",
        });
        return;
      }

      const location = results[0].geometry?.location;
      if (!location) {
        setSearchState({
          loading: false,
          error: "Location result did not include coordinates.",
        });
        return;
      }

      const nextLat = Number(location.lat().toFixed(6));
      const nextLng = Number(location.lng().toFixed(6));
      onLatitudeChangeRef.current(formatCoordinate(nextLat));
      onLongitudeChangeRef.current(formatCoordinate(nextLng));
      suppressAutocompleteRef.current = true;
      setSearchQuery(results[0].formatted_address || String(searchQuery || "").trim());
      setSuggestions([]);
      setShowSuggestions(false);
      setSearchState({ loading: false, error: "" });
    });
  };

  const handleSearch = () => {
    const query = String(searchQuery || "").trim();
    if (!query || !geocoderRef.current) return;
    updateCoordinatesFromGeocode({ address: query });
  };

  const handleSuggestionPick = (suggestion) => {
    suppressAutocompleteRef.current = true;
    setSearchQuery(suggestion.description);
    setSuggestions([]);
    setShowSuggestions(false);
    updateCoordinatesFromGeocode({ placeId: suggestion.placeId });
  };

  return (
    <div style={S.wrapper}>
      <div style={S.helperRow}>
        <span style={S.helperText}>
          Pick the birthplace from the map or search for a place. Coordinates are filled automatically.
        </span>
        {googleMapsLink ? (
          <a href={googleMapsLink} target="_blank" rel="noreferrer" style={S.link}>
            Open in Google Maps
          </a>
        ) : null}
      </div>

      <div style={S.searchRow}>
        <input
          style={S.input}
          value={searchQuery}
          onChange={(event) => {
            setSearchQuery(event.target.value);
            setShowSuggestions(true);
          }}
          onFocus={() => {
            if (suggestions.length) setShowSuggestions(true);
          }}
          onBlur={() => {
            window.setTimeout(() => setShowSuggestions(false), 150);
          }}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              handleSearch();
            }
          }}
          placeholder="Search place on map, e.g. Baramati, Pune"
        />
        <button type="button" style={S.searchBtn} onClick={handleSearch} disabled={!mapState.ready || searchState.loading}>
          {searchState.loading ? "Searching..." : "Search"}
        </button>
      </div>
      {showSuggestions && suggestions.length > 0 ? (
        <div style={S.suggestionList}>
          {suggestions.map((suggestion) => (
            <button
              key={suggestion.placeId}
              type="button"
              style={S.suggestionItem}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => handleSuggestionPick(suggestion)}
            >
              <span style={S.suggestionPrimary}>{suggestion.primaryText}</span>
              {suggestion.secondaryText ? <span style={S.suggestionSecondary}>{suggestion.secondaryText}</span> : null}
            </button>
          ))}
        </div>
      ) : null}
      {searchState.error ? <div style={S.searchError}>{searchState.error}</div> : null}

      <div style={S.coordGrid}>
        <div style={S.coordItem}>
          <span style={S.inputLabel}>Latitude</span>
          <span style={S.coordValue}>{latitude || "Not selected yet"}</span>
        </div>

        <div style={S.coordItem}>
          <span style={S.inputLabel}>Longitude</span>
          <span style={S.coordValue}>{longitude || "Not selected yet"}</span>
        </div>
      </div>

      {GOOGLE_MAPS_API_KEY ? (
        <div style={S.mapShell}>
          <div ref={mapNodeRef} style={S.mapCanvas} />
          {mapState.loading ? <div style={S.overlay}>Loading Google Map...</div> : null}
          {mapState.error ? <div style={S.overlay}>{mapState.error}</div> : null}
        </div>
      ) : (
        <div style={S.notice}>
          Add `REACT_APP_GOOGLE_MAPS_API_KEY` to `.env.local` to enable map-based coordinate selection.
        </div>
      )}

      <div style={S.footerRow}>
        <button
          type="button"
          style={S.clearBtn}
          onClick={() => {
            onLatitudeChange("");
            onLongitudeChange("");
          }}
        >
          Clear Coordinates
        </button>
        {mapState.ready ? <span style={S.status}>Click or drag the pin to update coordinates.</span> : null}
      </div>
    </div>
  );
}

const S = {
  wrapper: {
    display: "grid",
    gap: 12,
  },
  helperRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 12,
    flexWrap: "wrap",
  },
  helperText: {
    color: BRAND.muted,
    fontSize: 12,
    fontWeight: 700,
  },
  link: {
    color: BRAND.red,
    fontSize: 12,
    fontWeight: 800,
    textDecoration: "none",
  },
  coordGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
    gap: 10,
  },
  coordItem: {
    padding: "12px 14px",
    borderRadius: 16,
    border: `1px solid ${BRAND.border}`,
    background: "#fffaf8",
    display: "grid",
    gap: 6,
  },
  coordValue: {
    color: BRAND.text,
    fontSize: 15,
    fontWeight: 900,
    wordBreak: "break-word",
  },
  searchRow: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) auto",
    gap: 10,
    alignItems: "center",
  },
  searchBtn: {
    border: `1px solid ${BRAND.borderStrong}`,
    background: BRAND.red,
    color: "#fff",
    borderRadius: 12,
    padding: "10px 16px",
    fontSize: 13,
    fontWeight: 800,
    cursor: "pointer",
  },
  searchError: {
    color: BRAND.red,
    fontSize: 12,
    fontWeight: 700,
  },
  suggestionList: {
    marginTop: -4,
    border: `1px solid ${BRAND.border}`,
    borderRadius: 16,
    overflow: "hidden",
    background: "#fff",
    boxShadow: "0 18px 48px rgba(47,17,19,0.08)",
  },
  suggestionItem: {
    width: "100%",
    border: "none",
    borderBottom: `1px solid ${BRAND.borderSoft}`,
    background: "#fff",
    padding: "12px 14px",
    textAlign: "left",
    display: "grid",
    gap: 4,
    cursor: "pointer",
  },
  suggestionPrimary: {
    color: BRAND.text,
    fontSize: 14,
    fontWeight: 800,
  },
  suggestionSecondary: {
    color: BRAND.muted,
    fontSize: 12,
    fontWeight: 600,
  },
  inputLabel: {
    color: BRAND.muted,
    fontSize: 12,
    fontWeight: 800,
  },
  input: {
    width: "100%",
    borderRadius: 12,
    border: `1px solid ${BRAND.borderStrong}`,
    padding: "10px 12px",
    outline: "none",
    fontSize: 14,
    background: "rgba(255,255,255,0.94)",
  },
  mapShell: {
    position: "relative",
    borderRadius: 18,
    overflow: "hidden",
    border: `1px solid ${BRAND.border}`,
    minHeight: 280,
    background: "#f7f1ec",
  },
  mapCanvas: {
    width: "100%",
    height: 280,
  },
  overlay: {
    position: "absolute",
    inset: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "rgba(255,255,255,0.78)",
    color: BRAND.muted,
    fontSize: 13,
    fontWeight: 800,
    padding: 16,
    textAlign: "center",
  },
  notice: {
    borderRadius: 16,
    border: `1px dashed ${BRAND.borderStrong}`,
    background: BRAND.redSoftAlt,
    color: BRAND.muted,
    fontSize: 12,
    fontWeight: 700,
    padding: "12px 14px",
  },
  footerRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 12,
    flexWrap: "wrap",
  },
  clearBtn: {
    border: `1px solid ${BRAND.borderStrong}`,
    background: "#fff",
    color: BRAND.red,
    borderRadius: 999,
    padding: "8px 12px",
    fontSize: 12,
    fontWeight: 800,
    cursor: "pointer",
  },
  status: {
    color: BRAND.muted,
    fontSize: 12,
    fontWeight: 700,
  },
};
